import { Controller } from '@hotwired/stimulus';
import { describeRule } from '../../services/recurring_schedule/describe';
import { createPickerView, type PickerView } from '../../services/recurring_schedule/picker_view';
import { writePreset } from '../../services/recurring_schedule/preset';
import { parseRule, type RuleHash } from '../../services/recurring_schedule/rule';

export default class extends Controller {
  static targets = [
    'preset',
    'dialog',
    'frequency',
    'interval',
    'unit',
    'section',
    'day',
    'dayOfMonth',
    'week',
    'monthlyMode',
    'monthDayGrid',
    'monthWeekGrid',
    'summary',
  ];
  static values = { firstDay: Number };

  declare presetTarget: HTMLSelectElement;
  declare dialogTarget: HTMLDialogElement;
  declare frequencyTarget: HTMLSelectElement;
  declare intervalTarget: HTMLInputElement;
  declare unitTarget: HTMLElement;
  declare sectionTargets: HTMLElement[];
  declare dayTargets: HTMLButtonElement[];
  declare dayOfMonthTargets: HTMLButtonElement[];
  declare weekTargets: HTMLButtonElement[];
  declare monthlyModeTargets: HTMLInputElement[];
  declare monthDayGridTarget: HTMLElement;
  declare monthWeekGridTarget: HTMLElement;
  declare summaryTarget: HTMLElement;
  declare firstDayValue: number;

  private currentRule: RuleHash | null = null;
  private previousValue = '';
  private pickerView?: PickerView;

  connect(): void {
    this.currentRule = parseRule(this.presetTarget.value);
    this.previousValue = this.presetTarget.value;
    this.picker.applyUnit();
  }

  presetChanged(): void {
    if (this.presetTarget.value === 'custom') {
      this.open();
      return;
    }

    this.currentRule = parseRule(this.presetTarget.value);
    this.previousValue = this.presetTarget.value;
  }

  cancel(): void {
    this.dialogTarget.close();
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    this.save();
  }

  dismissOnBackdrop(event: MouseEvent): void {
    if (event.target !== this.dialogTarget) return;

    const { left, right, top, bottom } = this.dialogTarget.getBoundingClientRect();
    const inside =
      event.clientX >= left &&
      event.clientX <= right &&
      event.clientY >= top &&
      event.clientY <= bottom;

    if (!inside) this.cancel();
  }

  closed(): void {
    if (this.presetTarget.value === 'custom') {
      this.presetTarget.value = this.previousValue;
    }
  }

  save(): void {
    const rule = this.picker.rule();

    this.currentRule = rule;
    this.previousValue = writePreset(this.presetTarget, rule);
    this.presetTarget.value = this.previousValue;
    this.dialogTarget.close();
    this.presetTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  frequencyChanged(): void {
    this.picker.showSections();
    this.picker.applyUnit();
    this.refreshSummary();
  }

  monthlyModeChanged(): void {
    this.picker.showMonthlyMode();
    this.refreshSummary();
  }

  toggle(event: Event): void {
    this.picker.toggle(event.currentTarget as HTMLButtonElement);
    this.refreshSummary();
  }

  refreshSummary(): void {
    this.summaryTarget.textContent = describeRule(this.picker.rule());
  }

  private open(): void {
    this.picker.write(this.currentRule);
    this.refreshSummary();
    this.dialogTarget.showModal();
  }

  private get picker(): PickerView {
    this.pickerView ??= createPickerView(
      {
        frequency: this.frequencyTarget,
        interval: this.intervalTarget,
        unit: this.unitTarget,
        sections: this.sectionTargets,
        days: this.dayTargets,
        daysOfMonth: this.dayOfMonthTargets,
        weeks: this.weekTargets,
        monthlyModes: this.monthlyModeTargets,
        monthDayGrid: this.monthDayGridTarget,
        monthWeekGrid: this.monthWeekGridTarget,
      },
      this.firstDayValue,
    );

    return this.pickerView;
  }
}
